import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { SMLNavBadge } from './hooks/useNavBadges';
import type { CatalogSetupMissing } from './hooks/useCatalogSetupNeeded';
import { smlNavigationChromeMessages as messages } from './smlNavigationMessages';
import { workBadgeMessages } from './workBadgeMessages';
import styles from './NavBadge.module.scss';

/** The sentence of the setup dot names what is missing (QA 2026-10-28, imp-9, lit-11). */
function setupMessage(reason: CatalogSetupMissing | undefined) {
  if (reason === 'release') {
    return messages.catalogSetupRelease;
  }
  if (reason === 'artist') {
    return messages.catalogSetupArtist;
  }
  return messages.catalogSetup;
}

interface NavBadgeProps {
  badge: SMLNavBadge;
  className?: string;
}

/**
 * Design §5: the red attention count or the hollow violet setup dot. The mark itself is hidden
 * from assistive tech; a sentence in the link's name says what it means.
 */
const NavBadgeComponent: React.FC<NavBadgeProps> = ({ badge, className }) => {
  const intl = useIntl();
  const count = badge.kind === 'count' ? badge.count : 0;
  // The sentence follows the source. Catalog has a count (Reconnect) and a setup dot.
  const text = {
    failedPosts: intl.formatMessage(messages.failedPosts, { count }),
    catalogSetup:
      badge.kind === 'count'
        ? intl.formatMessage(messages.catalogReconnect, { count })
        : intl.formatMessage(setupMessage(badge.reason)),
    kitWork: intl.formatMessage(workBadgeMessages.failedText, { count }),
    outreachInbox: intl.formatMessage(messages.outreachInbox, { count }),
  }[badge.source];

  return (
    <>
      <span
        className={clsx(badge.kind === 'count' ? styles.count : styles.setup, className)}
        aria-hidden="true"
        data-testid={`nav-badge-${badge.source}`}
      >
        {badge.kind === 'count' ? badge.count : null}
      </span>
      <span className="visually-hidden">{text}</span>
    </>
  );
};

export const NavBadge = TypedMemo(NavBadgeComponent);
